import { useState, useEffect, useCallback, useRef } from 'react';
import { getInterviewsByCompanyId } from '../api/ApplicationService';
import { getDateRangeBounds } from '../utils/dateRange';
import { getWithExpiry, setWithExpiry } from '../utils/sessionStorage';

// VI-623 / VI-753 — completed-response totals for the "Responses (X)" tab and the
// "Showing N of Y responses" header.
//
// - `companyCompleted` is always the company-wide completed total for the selected
//   date range (drives the tab label, independent of any job filter).
// - `scopeCompleted` is the completed total for the CURRENT scope: the selected
//   job's total when a job is selected, otherwise the company total.
//
// VI-753: these counts now come from the SAME interviews LIST endpoint that feeds
// the grid — a lightweight `limit=1` request whose authoritative `total` reflects
// the full date-bounded (and, when scoped, job-bounded) Completed set. Previously
// this used the separate AS-8108 counts endpoint; two endpoints answering the same
// question could drift (that was the VI-749 root cause). Using one endpoint with
// identical params (companyId + status=Completed + the same getDateRangeBounds
// window [+ jobId]) makes the badge equal to the grid's total BY CONSTRUCTION, not
// by a backend equality guarantee. The company endpoint is date-bounded even for a
// single job (AS-8720 jobId support), so the job-scope count is authoritative and
// not subject to the job endpoint's 100-row cap.

const CACHE_TTL_MS = 5 * 60 * 1000; // 5 minutes, matching useInterviewManagement

interface ResponseCounts {
  companyCompleted: number;
  scopeCompleted: number;
  isLoading: boolean;
  error: string | null;
}

const cacheKey = (companyId: string | number, jobId: number | null, dateRange: string) =>
  `responseCounts_${companyId}_${jobId ?? 'company'}_${dateRange}`;

export const useResponseCounts = (
  companyId: string | number,
  jobId: number | null,
  dateRange: string
): ResponseCounts => {
  const [companyCompleted, setCompanyCompleted] = useState(0);
  const [jobCompleted, setJobCompleted] = useState<number | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isDisabled = !companyId || companyId === 0;

  // Guards against overlapping fetches clobbering each other's results.
  const requestIdRef = useRef(0);

  const fetchCompletedCount = useCallback(
    async (targetJobId: number | null): Promise<number> => {
      const key = cacheKey(companyId, targetJobId, dateRange);
      const cached = getWithExpiry(key) as number | null;
      if (cached !== null && cached !== undefined) {
        return cached;
      }

      const { startDate, endDate } = getDateRangeBounds(dateRange);
      // VI-753: read the authoritative `total` from the interviews LIST endpoint
      // (limit=1 — we only need the count, not the rows). Same endpoint + params as
      // the grid, so the badge and the grid can never disagree.
      const result = await getInterviewsByCompanyId(companyId, {
        page: 1,
        limit: 1,
        status: 'Completed',
        startDate,
        endDate,
        jobId: targetJobId ?? undefined,
      });

      const completed = result?.total ?? 0;
      setWithExpiry(key, completed, CACHE_TTL_MS);
      return completed;
    },
    [companyId, dateRange]
  );

  useEffect(() => {
    if (isDisabled) {
      setCompanyCompleted(0);
      setJobCompleted(null);
      return;
    }

    const requestId = ++requestIdRef.current;
    setIsLoading(true);
    setError(null);

    // Company total always; job total only when a job is selected.
    const tasks: Promise<void>[] = [
      fetchCompletedCount(null).then(count => {
        if (requestId === requestIdRef.current) setCompanyCompleted(count);
      }),
    ];

    if (jobId !== null && jobId !== undefined) {
      tasks.push(
        fetchCompletedCount(jobId).then(count => {
          if (requestId === requestIdRef.current) setJobCompleted(count);
        })
      );
    } else if (requestId === requestIdRef.current) {
      setJobCompleted(null);
    }

    Promise.all(tasks)
      .catch(err => {
        if (requestId === requestIdRef.current) {
          console.error('Error fetching response counts:', err);
          setError('Failed to load response counts');
        }
      })
      .finally(() => {
        if (requestId === requestIdRef.current) setIsLoading(false);
      });
  }, [companyId, jobId, dateRange, isDisabled, fetchCompletedCount]);

  return {
    companyCompleted,
    scopeCompleted: jobId !== null && jobId !== undefined ? (jobCompleted ?? 0) : companyCompleted,
    isLoading,
    error,
  };
};
